(function () {
  'use strict';

  var PER_PAGE = 20;
  var SHOPIFY_PER_PAGE = 50; // must match Liquid paginate value
  var VIRTUAL_PAGE_PARAM = 'vpage';
  var sectionId = '';
  var productList, pagContainer;

  // ── Flat product store ──
  // All visible product HTML strings in order, across all Shopify pages.
  // Virtual page N = allProducts.slice((N-1)*PER_PAGE, N*PER_PAGE)
  var allProducts = [];
  var lastFetchedShopifyPage = 0;  // highest Shopify page appended to allProducts
  var noMoreShopifyPages = false;  // true once we know there are no more products
  var currentVirtualPage = 1;
  var estimatedTotalPages = null;

  // ── Fetch infrastructure ──
  var fetchedPageCache = {};       // shopifyPage -> array of product HTML strings
  var inflightFetches = {};        // shopifyPage -> Promise (dedup concurrent fetches)
  var seenProductIds = {};         // productId -> true for aggregated virtual list dedupe
  var orDerivedTotalProducts = 0;  // max totalProducts seen across OR sub-URL responses

  // ── State ──
  var imageColumnHtml = '';        // HTML of image-column block(s) to prepend on each page
  var totalVisibleProducts = null; // exact count after background scan
  var filterCountCache = {};       // filterUrl -> visible product count
  var filterCountAbortController = null; // AbortController for in-flight filter count fetches

  function getProductIdFromHtml(html) {
    var match = html && html.match(/data-product-id="([^"]+)"/);
    return match ? match[1] : '';
  }

  // ── Randomization ──
  // Returns true when no explicit sort_by is set (i.e. default/manual order)
  function shouldRandomize() {
    var params = new URLSearchParams(window.location.search);
    var sort = params.get('sort_by');
    return !sort || sort === 'manual';
  }

  // Seeded pseudo-random number generator (mulberry32) — seed derived from collection path
  function getSessionSeed() {
    var str = window.location.pathname;
    var seed = 0;
    for (var i = 0; i < str.length; i++) {
      seed = (Math.imul(31, seed) + str.charCodeAt(i)) | 0;
    }
    return seed >>> 0 || 1;
  }

  function seededRandFn(seed) {
    return function () {
      seed = (seed + 0x6D2B79F5) | 0;
      var t = Math.imul(seed ^ seed >>> 15, 1 | seed);
      t = (t + Math.imul(t ^ t >>> 7, 61 | t)) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  function shuffleProducts() {
    if (!shouldRandomize()) return;
    var rand = seededRandFn(getSessionSeed());
    for (var i = allProducts.length - 1; i > 0; i--) {
      var j = Math.floor(rand() * (i + 1));
      var tmp = allProducts[i];
      allProducts[i] = allProducts[j];
      allProducts[j] = tmp;
    }
  }

  function appendUniqueProducts(products) {
    var added = 0;

    for (var i = 0; i < products.length; i++) {
      var productHtml = products[i];
      var productId = getProductIdFromHtml(productHtml);

      if (productId) {
        if (seenProductIds[productId]) continue;
        seenProductIds[productId] = true;
      }

      allProducts.push(productHtml);
      added++;
    }

    return added;
  }

  // Parse filter.p.tag values from current URL, grouped by prefix.
  // Returns { orGroups: [[tag, tag], ...], andTags: [tag, ...] }
  // orGroups contains prefix groups with >1 value (need OR); andTags has single-value groups.
  function parseTagOrGroups() {
    var params = new URLSearchParams(window.location.search);
    var tagValues = params.getAll('filter.p.tag');
    var prefixMap = {};
    for (var i = 0; i < tagValues.length; i++) {
      var colonIdx = tagValues[i].indexOf(':');
      var prefix = colonIdx > -1 ? tagValues[i].substring(0, colonIdx) : tagValues[i];
      if (!prefixMap[prefix]) prefixMap[prefix] = [];
      prefixMap[prefix].push(tagValues[i]);
    }
    var orGroups = [], andTags = [];
    for (var key in prefixMap) {
      if (prefixMap[key].length > 1) orGroups.push(prefixMap[key]);
      else andTags.push(prefixMap[key][0]);
    }
    return { orGroups: orGroups, andTags: andTags };
  }

  function hasOrFilters() {
    return parseTagOrGroups().orGroups.length > 0;
  }

  // Returns array of fetch URLs for a given Shopify page.
  // Without OR filters: single URL. With OR: cartesian product URLs, capped at 4.
  function getSectionUrls(pageNum) {
    var base = new URL(window.location.href);
    var parsed = parseTagOrGroups();

    // Base params: everything except filter.p.tag, vpage, and page
    var baseParams = new URLSearchParams();
    new URLSearchParams(window.location.search).forEach(function (val, key) {
      if (key !== 'filter.p.tag' && key !== VIRTUAL_PAGE_PARAM && key !== 'page') {
        baseParams.append(key, val);
      }
    });
    // Add single-value (AND) tag params
    for (var i = 0; i < parsed.andTags.length; i++) {
      baseParams.append('filter.p.tag', parsed.andTags[i]);
    }
    baseParams.set('page', pageNum);
    if (sectionId) baseParams.set('section_id', sectionId);

    if (parsed.orGroups.length === 0) {
      return [base.origin + base.pathname + '?' + baseParams.toString()];
    }

    // Build cartesian product of OR groups, capped at 4 combinations
    var combos = [[]];
    for (var g = 0; g < parsed.orGroups.length; g++) {
      var next = [];
      for (var c = 0; c < combos.length; c++) {
        for (var v = 0; v < parsed.orGroups[g].length; v++) {
          next.push(combos[c].concat([parsed.orGroups[g][v]]));
          if (next.length >= 4) break;
        }
        if (next.length >= 4) break;
      }
      combos = next;
    }

    return combos.map(function (combo) {
      var p = new URLSearchParams(baseParams.toString());
      for (var i = 0; i < combo.length; i++) p.append('filter.p.tag', combo[i]);
      return base.origin + base.pathname + '?' + p.toString();
    });
  }

  function getVirtualPageFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var page = parseInt(params.get(VIRTUAL_PAGE_PARAM), 10);
    if (isNaN(page) || page < 1) return 1;
    return page;
  }

  function getVirtualPageHref(pageNum) {
    var url = new URL(window.location.href);
    if (pageNum > 1) {
      url.searchParams.set(VIRTUAL_PAGE_PARAM, pageNum);
    } else {
      url.searchParams.delete(VIRTUAL_PAGE_PARAM);
    }
    return url.pathname + url.search;
  }

  function syncVirtualPageInUrl(pageNum, historyMode) {
    if (historyMode === 'none') return;

    var nextUrl = getVirtualPageHref(pageNum);
    var currentUrl = window.location.pathname + window.location.search;
    if (nextUrl === currentUrl) return;

    if (historyMode === 'replace') {
      window.history.replaceState({ vpage: pageNum }, '', nextUrl);
      return;
    }

    window.history.pushState({ vpage: pageNum }, '', nextUrl);
  }

  // Fetch a Shopify page — returns array of product outerHTML strings.
  // When OR filters are active, fetches multiple URLs and unions results by product ID.
  // Caches results and deduplicates concurrent requests for the same page.
  function fetchShopifyPage(pageNum) {
    if (fetchedPageCache[pageNum] !== undefined) return Promise.resolve(fetchedPageCache[pageNum]);
    if (inflightFetches[pageNum]) return inflightFetches[pageNum];

    var urls = getSectionUrls(pageNum);

    function fetchOneUrl(url) {
      return fetch(url)
        .then(function (resp) { return resp.ok ? resp.text() : ''; })
        .then(function (html) {
          if (!html) return { items: [], total: 0 };
          var tpl = document.createElement('template');
          tpl.innerHTML = html;
          var cont = tpl.content.querySelector('#ProductGridContainer');
          var total = cont ? parseInt(cont.dataset.totalProducts, 10) || 0 : 0;
          var list = tpl.content.querySelector('#ProductsList');
          if (!list) return { items: [], total: total };
          var items = list.querySelectorAll(':scope > .product-column');
          var result = [];
          for (var i = 0; i < items.length; i++) result.push(items[i].outerHTML);
          return { items: result, total: total };
        })
        .catch(function () { return { items: [], total: 0 }; });
    }

    inflightFetches[pageNum] = Promise.all(urls.map(fetchOneUrl))
      .then(function (results) {
        // Track the max totalProducts across OR sub-URLs (used to determine scan depth)
        for (var r = 0; r < results.length; r++) {
          if (results[r].total > orDerivedTotalProducts) orDerivedTotalProducts = results[r].total;
        }

        if (results.length === 1) {
          fetchedPageCache[pageNum] = results[0].items;
          return results[0].items;
        }

        // Union items by product ID
        var seen = {}, merged = [];
        for (var r2 = 0; r2 < results.length; r2++) {
          for (var i = 0; i < results[r2].items.length; i++) {
            var id = getProductIdFromHtml(results[r2].items[i]);
            if (!id || !seen[id]) {
              if (id) seen[id] = true;
              merged.push(results[r2].items[i]);
            }
          }
        }
        fetchedPageCache[pageNum] = merged;
        return merged;
      })
      .catch(function () { fetchedPageCache[pageNum] = []; return []; })
      .finally(function () { delete inflightFetches[pageNum]; });

    return inflightFetches[pageNum];
  }

  // Ensure allProducts has at least upToIndex items by fetching Shopify pages sequentially.
  // Pages already in fetchedPageCache (e.g. from background scan) resolve instantly.
  async function ensureProducts(upToIndex) {
    while (allProducts.length < upToIndex && !noMoreShopifyPages) {
      var nextPage = lastFetchedShopifyPage + 1;
      var products = await fetchShopifyPage(nextPage);
      // Guard against background scan advancing lastFetchedShopifyPage during await
      if (nextPage <= lastFetchedShopifyPage) continue;
      lastFetchedShopifyPage = nextPage;
      if (products.length === 0) {
        noMoreShopifyPages = true;
        break;
      }
      appendUniqueProducts(products);
      // If the current virtual page was underfilled (e.g. Liquid only rendered 2/20 due to
      // configurable filtering), re-render it now that more products are available.
      refillCurrentPageIfUnderfilled();
    }
  }

  // Re-render the current virtual page if more products are now available than displayed.
  // Safe to call repeatedly — only writes DOM when count actually grew.
  function refillCurrentPageIfUnderfilled() {
    if (!productList) return;
    var displayed = productList.querySelectorAll(':scope > .product-column').length;
    var pageProducts = getVirtualPageProducts(currentVirtualPage);
    if (pageProducts.length > displayed) {
      renderProducts(pageProducts);
      renderPagination(hasNextVirtualPage(currentVirtualPage));
    }
  }

  // Get products for a specific virtual page (1-indexed)
  function getVirtualPageProducts(pageNum) {
    var start = (pageNum - 1) * PER_PAGE;
    return allProducts.slice(start, start + PER_PAGE);
  }

  // Check if there's a next virtual page beyond pageNum
  function hasNextVirtualPage(pageNum) {
    return allProducts.length > pageNum * PER_PAGE || !noMoreShopifyPages;
  }

  // Render products from HTML strings using a single innerHTML write
  function renderProducts(htmlArr) {
    var out = imageColumnHtml ? imageColumnHtml + htmlArr.join('') : htmlArr.join('');
    productList.innerHTML = out;
  }

  // Set ProductCount text with Polish pluralization
  function setProductCountText(count) {
    var el = document.getElementById('ProductCount');
    if (!el) return;
    if (count === 1) {
      el.textContent = '1 produkt';
    } else if (count >= 2 && count <= 4) {
      el.textContent = count + ' produkty';
    } else {
      el.textContent = count + ' produktów';
    }
  }

  // Background-scan ALL Shopify pages to get exact visible product count.
  // After completion, populates allProducts fully so all virtual pages are instant.
  function countAllVisibleProducts(shopifyTotalProducts) {
    var totalShopifyPages = Math.ceil(shopifyTotalProducts / SHOPIFY_PER_PAGE) || 1;

    var promises = [];
    for (var p = 1; p <= totalShopifyPages; p++) {
      promises.push(fetchShopifyPage(p));
    }

    Promise.all(promises).then(function (results) {
      // Rebuild allProducts from all cached pages in sequential order.
      // This is synchronous — no race conditions with ensureProducts.
      allProducts = [];
      seenProductIds = {};
      lastFetchedShopifyPage = 0;
      for (var p = 1; p <= totalShopifyPages; p++) {
        var cached = fetchedPageCache[p];
        if (!cached) break;
        appendUniqueProducts(cached);
        lastFetchedShopifyPage = p;
      }
      noMoreShopifyPages = true;
      totalVisibleProducts = allProducts.length;
      setProductCountText(totalVisibleProducts);

      if (PER_PAGE > 0) {
        estimatedTotalPages = Math.ceil(totalVisibleProducts / PER_PAGE);
      }

      shuffleProducts();

      // Re-render pagination with exact page count
      renderPagination(hasNextVirtualPage(currentVirtualPage));

      // Re-render the current virtual page if it was underfilled
      // (e.g. Liquid only rendered 2/20 due to configurable filtering on Shopify page 1).
      refillCurrentPageIfUnderfilled();

      // Count per-filter-value
      countFilterValues();
    });
  }

  // Hide all .count spans immediately — will be filled in by JS
  function hideFilterCounts() {
    // Abort any in-flight filter count fetches to free up browser connections
    if (filterCountAbortController) filterCountAbortController.abort();
    filterCountAbortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

    var counts = document.querySelectorAll('.facet-accordion-details .count');
    for (var i = 0; i < counts.length; i++) {
      counts[i].textContent = '';
      counts[i].removeAttribute('data-counted');
    }
  }

  // Filter count: fetch pages to count visible products for a filter URL.
  // For small collections (≤3 Shopify pages), does full scan for exact count.
  // For larger, estimates from page 1 ratio.
  function fetchFilterCountFast(filterUrl, signal) {
    if (filterCountCache[filterUrl] !== undefined) {
      return Promise.resolve(filterCountCache[filterUrl]);
    }

    var baseFilterUrl = filterUrl + (filterUrl.indexOf('?') > -1 ? '&' : '?');
    var sectionParam = sectionId ? '&section_id=' + sectionId : '';
    var fetchOpts = signal ? { signal: signal } : {};

    return fetch(baseFilterUrl + 'page=1' + sectionParam, fetchOpts)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        var container = tpl.content.querySelector('#ProductGridContainer');
        var shopifyTotal = container ? parseInt(container.dataset.totalProducts, 10) || 0 : 0;
        if (shopifyTotal === 0) {
          filterCountCache[filterUrl] = 0;
          return 0;
        }

        var list = tpl.content.querySelector('#ProductsList');
        var visibleOnPage1 = list ? list.querySelectorAll(':scope > .product-column').length : 0;

        if (shopifyTotal <= SHOPIFY_PER_PAGE) {
          filterCountCache[filterUrl] = visibleOnPage1;
          return visibleOnPage1;
        }

        var totalShopifyPages = Math.ceil(shopifyTotal / SHOPIFY_PER_PAGE);

        if (totalShopifyPages <= 3) {
          var promises = [];
          for (var p = 2; p <= totalShopifyPages; p++) {
            promises.push(
              fetch(baseFilterUrl + 'page=' + p + sectionParam, fetchOpts)
                .then(function (r) { return r.text(); })
                .then(function (h) {
                  var t = document.createElement('template');
                  t.innerHTML = h;
                  var l = t.content.querySelector('#ProductsList');
                  return l ? l.querySelectorAll(':scope > .product-column').length : 0;
                })
                .catch(function (err) { return (err && err.name === 'AbortError') ? null : 0; })
            );
          }
          return Promise.all(promises).then(function (counts) {
            var total = visibleOnPage1;
            for (var c = 0; c < counts.length; c++) {
              if (counts[c] === null) return null; // aborted — don't cache
              total += counts[c];
            }
            filterCountCache[filterUrl] = total;
            return total;
          });
        }

        var shopifyOnPage1 = Math.min(SHOPIFY_PER_PAGE, shopifyTotal);
        var ratio = shopifyOnPage1 > 0 ? visibleOnPage1 / shopifyOnPage1 : 0;
        var estimated = Math.round(ratio * shopifyTotal);
        filterCountCache[filterUrl] = estimated;
        return estimated;
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return null; // aborted — don't cache
        filterCountCache[filterUrl] = 0;
        return 0;
      });
  }

  function setCountSpan(span, count) {
    if (!span) return;
    span.textContent = count;
    span.setAttribute('data-counted', '');
  }

  function findCountSpan(cb) {
    var li = cb.closest('li');
    if (li) return li.querySelector('.count');
    var span = cb.parentElement.querySelector('.count');
    if (span) return span;
    var label = cb.parentElement.querySelector('label');
    return label ? label.querySelector('.count') : null;
  }

  function countFilterValues() {
    var signal = filterCountAbortController ? filterCountAbortController.signal : null;
    var checkboxes = document.querySelectorAll('.facet-accordion-details input[type="checkbox"][name^="filter."]');
    var seen = {};
    var tasks = [];
    var baseUrl = window.location.pathname;
    var currentParams = new URLSearchParams(window.location.search);
    var allChecked = document.querySelectorAll('.facet-accordion-details input[type="checkbox"][name^="filter."]:checked');

    for (var i = 0; i < checkboxes.length; i++) {
      var cb = checkboxes[i];
      var name = cb.name;
      var value = cb.value;
      var key = name + '=' + value;

      if (seen[key]) {
        seen[key].push(cb);
        continue;
      }
      seen[key] = [cb];

      if (cb.checked) {
        if (totalVisibleProducts !== null) {
          var entries0 = seen[key];
          for (var e0 = 0; e0 < entries0.length; e0++) {
            setCountSpan(findCountSpan(entries0[e0]), totalVisibleProducts);
          }
        }
        continue;
      }

      // For filter.p.tag: determine prefix so we can apply OR logic within same group
      var candidatePrefix = null;
      if (name === 'filter.p.tag') {
        var tagParts = value.split(':');
        candidatePrefix = tagParts[0] ? tagParts[0].toLowerCase() : null;
      }

      var params = new URLSearchParams();
      if (currentParams.has('q')) params.set('q', currentParams.get('q'));
      if (currentParams.has('type')) params.set('type', currentParams.get('type'));
      if (currentParams.has('sort_by')) params.set('sort_by', currentParams.get('sort_by'));
      for (var j = 0; j < allChecked.length; j++) {
        // OR-aware: skip checked tags from the same prefix group
        // so the count reflects this value standalone (not AND-combined with sibling values)
        if (candidatePrefix !== null && allChecked[j].name === 'filter.p.tag') {
          var checkedTagParts = allChecked[j].value.split(':');
          var checkedPrefix = checkedTagParts[0] ? checkedTagParts[0].toLowerCase() : null;
          if (checkedPrefix === candidatePrefix) continue;
        }
        params.append(allChecked[j].name, allChecked[j].value);
      }
      params.append(name, value);
      if (currentParams.has('filter.v.price.gte')) params.set('filter.v.price.gte', currentParams.get('filter.v.price.gte'));
      if (currentParams.has('filter.v.price.lte')) params.set('filter.v.price.lte', currentParams.get('filter.v.price.lte'));

      var filterUrl = baseUrl + '?' + params.toString();

      (function (filterKey, url) {
        tasks.push(
          fetchFilterCountFast(url, signal).then(function (count) {
            if (count === null) return; // aborted — skip
            var entries = seen[filterKey];
            for (var e = 0; e < entries.length; e++) {
              setCountSpan(findCountSpan(entries[e]), count);
            }
          })
        );
      })(key, filterUrl);
    }
  }

  var prevSvg = '<svg class="icon icon-caret-left rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
  var nextSvg = '<svg class="icon icon-caret-right rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  function renderPagination(hasNext) {
    var hasPrev = currentVirtualPage > 1;
    if (!hasPrev && !hasNext) { pagContainer.innerHTML = ''; return; }

    var lastPage = null;
    if (!hasNext) {
      lastPage = currentVirtualPage;
    } else if (estimatedTotalPages) {
      lastPage = estimatedTotalPages;
    }

    var pages = [];
    pages.push(1);
    for (var i = currentVirtualPage - 2; i <= currentVirtualPage + 2; i++) {
      if (i > 1) pages.push(i);
    }
    if (lastPage && lastPage > 1) pages.push(lastPage);

    var unique = {};
    for (var u = 0; u < pages.length; u++) {
      var pg = pages[u];
      if (lastPage && pg > lastPage) continue;
      unique[pg] = true;
    }
    pages = Object.keys(unique).map(Number).sort(function (a, b) { return a - b; });

    var i18n = window.configurableGridI18n || {};
    var parts = ['<nav class="pagination" style="margin-top:30px"><ul class="pagination__list list-unstyled flex items-center flex-wrap justify-center" role="list">'];

    if (hasPrev) {
      var prevPage = currentVirtualPage - 1;
      parts.push('<li><a href="' + getVirtualPageHref(prevPage) + '" data-page="' + prevPage + '" class="pagination__item pagination__item--previous pagination__item-arrow motion-reduce">' + prevSvg + ' <span>' + i18n.previous + '</span></a></li>');
    }

    var lastShown = 0;
    for (var pi = 0; pi < pages.length; pi++) {
      var p = pages[pi];
      if (lastShown > 0 && p - lastShown > 1) {
        parts.push('<li><span class="pagination__item">\u2026</span></li>');
      }
      if (p === currentVirtualPage) {
        var pageLabel = (i18n.page || 'Strona __N__').replace('__N__', p);
        parts.push('<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current background-2" aria-current="page" aria-label="' + pageLabel + '">' + p + '</a></li>');
      } else {
        var pageLabel = (i18n.page || 'Strona __N__').replace('__N__', p);
        parts.push('<li><a href="' + getVirtualPageHref(p) + '" data-page="' + p + '" class="pagination__item" aria-label="' + pageLabel + '">' + p + '</a></li>');
      }
      lastShown = p;
    }

    if (hasNext && !lastPage) {
      parts.push('<li><span class="pagination__item">\u2026</span></li>');
    }

    if (hasNext) {
      var nextPage = currentVirtualPage + 1;
      parts.push('<li><a href="' + getVirtualPageHref(nextPage) + '" data-page="' + nextPage + '" class="pagination__item pagination__item--next pagination__item-arrow motion-reduce"><span>' + i18n.next + '</span> ' + nextSvg + '</a></li>');
    }

    parts.push('</ul></nav>');
    pagContainer.innerHTML = parts.join('');

    pagContainer.onclick = function (e) {
      var link = e.target.closest('[data-page]');
      if (link) { e.preventDefault(); goToPage(parseInt(link.dataset.page, 10)); }
    };
  }

  function scrollToGrid() {
    var grid = document.getElementById('ProductGridContainer');
    if (grid) {
      var top = grid.getBoundingClientRect().top + window.scrollY - 200;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
  }

  function showLoader(show) {
    if (show) {
      productList.style.opacity = '0.3';
      productList.style.pointerEvents = 'none';
    } else {
      productList.style.opacity = '';
      productList.style.pointerEvents = '';
    }
  }

  async function goToPage(pageNum) {
    var opts = arguments.length > 1 && arguments[1] ? arguments[1] : {};
    var historyMode = opts.historyMode || 'push';
    var shouldScroll = opts.scroll !== false;

    if (pageNum < 1) return;
    currentVirtualPage = pageNum;

    var neededIndex = pageNum * PER_PAGE;
    var products = getVirtualPageProducts(pageNum);

    // If we already have enough products for this page, render immediately
    if (products.length > 0 && (products.length >= PER_PAGE || noMoreShopifyPages)) {
      renderProducts(products);
      renderPagination(hasNextVirtualPage(pageNum));
      syncVirtualPageInUrl(currentVirtualPage, historyMode);
      if (shouldScroll) scrollToGrid();
      // Prefetch next page's data in background
      ensureProducts((pageNum + 1) * PER_PAGE);
      return;
    }

    // Need to fetch more Shopify pages
    showLoader(true);
    await ensureProducts(neededIndex);
    showLoader(false);

    products = getVirtualPageProducts(pageNum);
    if (products.length === 0 && pageNum > 1) {
      currentVirtualPage = pageNum - 1;
      syncVirtualPageInUrl(currentVirtualPage, historyMode);
      return;
    }

    renderProducts(products);
    renderPagination(hasNextVirtualPage(pageNum));
    syncVirtualPageInUrl(currentVirtualPage, historyMode);
    if (shouldScroll) scrollToGrid();
    // Prefetch next page's data in background
    ensureProducts((pageNum + 1) * PER_PAGE);
  }

  function init() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    productList = container.querySelector('#ProductsList');
    if (!productList) return;

    pagContainer = document.getElementById('ConfigurablePagination');
    if (!pagContainer) {
      pagContainer = document.createElement('div');
      pagContainer.id = 'ConfigurablePagination';
      productList.parentNode.insertBefore(pagContainer, productList.nextSibling);
    }

    PER_PAGE = parseInt(container.dataset.productsPerPage, 10) || 20;
    sectionId = container.dataset.sectionId || '';
    var totalProducts = parseInt(container.dataset.totalProducts, 10) || 0;

    if (!document.getElementById('cfgSpinStyle')) {
      var st = document.createElement('style');
      st.id = 'cfgSpinStyle';
      st.textContent = '@keyframes cfgspin{to{transform:rotate(360deg)}}';
      document.head.appendChild(st);
    }

    // Capture image-column blocks from initial DOM to re-insert on every page
    var imageColumns = productList.querySelectorAll(':scope > .image-column');
    var imgParts = [];
    for (var ic = 0; ic < imageColumns.length; ic++) imgParts.push(imageColumns[ic].outerHTML);
    imageColumnHtml = imgParts.join('');

    if (imageColumns.length > 0) {
      PER_PAGE = PER_PAGE - imageColumns.length;
      if (PER_PAGE < 1) PER_PAGE = 1;
    }

    if (hasOrFilters()) {
      // OR filters active: DOM was server-rendered with AND logic — fetch page 1 with OR logic
      // Clear stale AND count immediately so user doesn't see wrong number
      var cntElInit = document.getElementById('ProductCount');
      if (cntElInit) cntElInit.textContent = '';

      // Hide server-rendered empty-state immediately — it's based on AND logic, not OR
      var orEmptyState = container.querySelector('.empty-state');
      if (orEmptyState) orEmptyState.style.display = 'none';

      showLoader(true);
      fetchShopifyPage(1).then(function (orProducts) {
        showLoader(false);
        allProducts = [];
        seenProductIds = {};
        appendUniqueProducts(orProducts);
        lastFetchedShopifyPage = 1;

        var effectiveTotal = orDerivedTotalProducts || 0;
        // Scan depth = pages in the largest OR sub-URL
        var scanPages = effectiveTotal > 0 ? Math.ceil(effectiveTotal / SHOPIFY_PER_PAGE) : 1;
        if (effectiveTotal > 0 && effectiveTotal <= SHOPIFY_PER_PAGE) noMoreShopifyPages = true;

        if (PER_PAGE > 0) {
          // Extrapolate from page-1 visible count; background scan will correct this
          var orEstInit = orProducts.length > 0 ? orProducts.length * scanPages : 0;
          estimatedTotalPages = orEstInit > 0 ? Math.ceil(orEstInit / PER_PAGE) : null;
        }
        // Show minimum confirmed count — background scan (countAllVisibleProducts) corrects to exact
        setProductCountText(orProducts.length);

        if (orProducts.length === 0 && effectiveTotal === 0) {
          noMoreShopifyPages = true;
        }

        if (allProducts.length > 0) {
          renderProducts(getVirtualPageProducts(1));
          renderPagination(hasNextVirtualPage(1));
          var initVP = getVirtualPageFromUrl();
          if (initVP > 1) {
            goToPage(initVP, { historyMode: 'replace', scroll: false });
          } else {
            syncVirtualPageInUrl(1, 'replace');
          }
          ensureProducts(2 * PER_PAGE);
        } else {
          setProductCountText(0);
          pagContainer.innerHTML = '';
          // OR filters returned 0 products — show the empty state
          if (orEmptyState) orEmptyState.style.display = '';
          syncVirtualPageInUrl(1, 'replace');
        }

        hideFilterCounts();
        countFilterValues();
        if (effectiveTotal > 0) countAllVisibleProducts(effectiveTotal);
        else {
          // effectiveTotal unknown (data-total-products missing) — mark done with what we have
          noMoreShopifyPages = true;
          totalVisibleProducts = allProducts.length;
          setProductCountText(totalVisibleProducts);
          if (PER_PAGE > 0) estimatedTotalPages = Math.ceil(totalVisibleProducts / PER_PAGE);
          renderPagination(hasNextVirtualPage(currentVirtualPage));
        }
      });
    } else {
      // Normal: seed allProducts from initial DOM (Shopify page 1, already filtered by Liquid)
      var initialItems = productList.querySelectorAll(':scope > .product-column');
      allProducts = [];
      seenProductIds = {};
      for (var i = 0; i < initialItems.length; i++) {
        appendUniqueProducts([initialItems[i].outerHTML]);
      }
      fetchedPageCache[1] = allProducts.slice();
      lastFetchedShopifyPage = 1;

      // If all Shopify products fit on page 1, no more pages to fetch
      if (totalProducts <= SHOPIFY_PER_PAGE) {
        noMoreShopifyPages = true;
      }

      if (totalProducts > 0 && PER_PAGE > 0) {
        if (noMoreShopifyPages) {
          // Single Shopify page — DOM already has all visible products, count is exact
          totalVisibleProducts = allProducts.length;
          setProductCountText(totalVisibleProducts);
          estimatedTotalPages = Math.ceil(totalVisibleProducts / PER_PAGE);
        } else {
          // Multi-page: ratio estimate is unreliable due to configurable product filtering.
          // Compute estimated pages for pagination only; background scan sets the accurate count.
          var shopifyOnPage1 = Math.min(SHOPIFY_PER_PAGE, totalProducts);
          var visibleOnPage1 = allProducts.length;
          var ratio = shopifyOnPage1 > 0 ? visibleOnPage1 / shopifyOnPage1 : 1;
          var estimatedVisibleTotal = Math.max(visibleOnPage1, Math.round(totalProducts * ratio));
          estimatedTotalPages = Math.ceil(estimatedVisibleTotal / PER_PAGE);
          // Do not show estimate — countAllVisibleProducts will set the accurate count
        }
      }

      // Render first virtual page
      if (allProducts.length > 0) {
        var firstPageProducts = getVirtualPageProducts(1);
        renderProducts(firstPageProducts);
        renderPagination(hasNextVirtualPage(1));

        var initialVirtualPage = getVirtualPageFromUrl();
        if (initialVirtualPage > 1) {
          goToPage(initialVirtualPage, { historyMode: 'replace', scroll: false });
        } else {
          syncVirtualPageInUrl(1, 'replace');
        }

        // Prefetch data for page 2 in background
        ensureProducts(2 * PER_PAGE);
      } else {
        syncVirtualPageInUrl(1, 'replace');
      }

      // Hide wrong Shopify filter counts and start counting
      hideFilterCounts();
      countFilterValues();

      // Background scan: count ALL visible products across all Shopify pages
      if (totalProducts > 0) {
        countAllVisibleProducts(totalProducts);
      }
    }

    // Listen for facets.js filter/sort updates — re-init the grid
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, function () {
        // Reset all state — filters changed, products are different
        allProducts = [];
        lastFetchedShopifyPage = 0;
        noMoreShopifyPages = false;
        currentVirtualPage = 1;
        syncVirtualPageInUrl(1, 'replace');
        estimatedTotalPages = null;
        fetchedPageCache = {};
        inflightFetches = {};
        seenProductIds = {};
        totalVisibleProducts = null;
        filterCountCache = {};
        orDerivedTotalProducts = 0;

        hideFilterCounts();

        // Re-read DOM elements (facets.js replaced them)
        var newContainer = document.getElementById('ProductGridContainer');
        if (!newContainer) return;
        productList = newContainer.querySelector('#ProductsList');
        if (!productList) return;
        pagContainer = document.getElementById('ConfigurablePagination');

        var newTotalProducts = parseInt(newContainer.dataset.totalProducts, 10) || 0;

        // Capture image columns from fresh DOM
        var imgCols = productList.querySelectorAll(':scope > .image-column');
        var imgP = [];
        for (var ic2 = 0; ic2 < imgCols.length; ic2++) imgP.push(imgCols[ic2].outerHTML);
        imageColumnHtml = imgP.join('');

        if (hasOrFilters()) {
          // OR filters active: DOM has AND results — skip and re-fetch with OR logic
          // Clear stale AND count immediately so user doesn't see wrong number
          var cntElFU = document.getElementById('ProductCount');
          if (cntElFU) cntElFU.textContent = '';

          // Hide server-rendered empty-state immediately — it's based on AND logic, not OR
          var orEmptyStateFU = newContainer.querySelector('.empty-state');
          if (orEmptyStateFU) orEmptyStateFU.style.display = 'none';

          showLoader(true);
          fetchShopifyPage(1).then(function (orProducts) {
            showLoader(false);
            allProducts = [];
            seenProductIds = {};
            appendUniqueProducts(orProducts);
            lastFetchedShopifyPage = 1;

            var effectiveTotal = orDerivedTotalProducts || 0;
            // Scan depth = pages in the largest OR sub-URL
            var scanPagesFU = effectiveTotal > 0 ? Math.ceil(effectiveTotal / SHOPIFY_PER_PAGE) : 1;
            if (effectiveTotal > 0 && effectiveTotal <= SHOPIFY_PER_PAGE) noMoreShopifyPages = true;

            if (PER_PAGE > 0) {
              // Extrapolate from page-1 visible count; background scan will correct this
              var orEstFU = orProducts.length > 0 ? orProducts.length * scanPagesFU : 0;
              estimatedTotalPages = orEstFU > 0 ? Math.ceil(orEstFU / PER_PAGE) : null;
            }
            // Show minimum confirmed count — background scan corrects to exact
            setProductCountText(orProducts.length);

            if (orProducts.length === 0 && effectiveTotal === 0) {
              noMoreShopifyPages = true;
            }

            if (allProducts.length > 0) {
              renderProducts(getVirtualPageProducts(1));
              renderPagination(hasNextVirtualPage(1));
              syncVirtualPageInUrl(1, 'replace');
              ensureProducts(2 * PER_PAGE);
            } else {
              setProductCountText(0);
              pagContainer.innerHTML = '';
              // OR filters returned 0 products — show the empty state
              if (orEmptyStateFU) orEmptyStateFU.style.display = '';
              syncVirtualPageInUrl(1, 'replace');
            }

            hideFilterCounts();
            countFilterValues();
            if (effectiveTotal > 0) countAllVisibleProducts(effectiveTotal);
            else {
              // effectiveTotal unknown — mark done with what we have
              noMoreShopifyPages = true;
              totalVisibleProducts = allProducts.length;
              setProductCountText(totalVisibleProducts);
              if (PER_PAGE > 0) estimatedTotalPages = Math.ceil(totalVisibleProducts / PER_PAGE);
              renderPagination(hasNextVirtualPage(currentVirtualPage));
            }
          });
        } else {
          // Normal: seed allProducts from fresh DOM
          var newItems = productList.querySelectorAll(':scope > .product-column');
          allProducts = [];
          seenProductIds = {};
          for (var j = 0; j < newItems.length; j++) {
            appendUniqueProducts([newItems[j].outerHTML]);
          }
          fetchedPageCache[1] = allProducts.slice();
          lastFetchedShopifyPage = 1;

          if (newTotalProducts <= SHOPIFY_PER_PAGE) {
            noMoreShopifyPages = true;
          }

          if (newTotalProducts > 0 && PER_PAGE > 0) {
            if (noMoreShopifyPages) {
              // Single Shopify page — exact count known from DOM
              totalVisibleProducts = allProducts.length;
              setProductCountText(totalVisibleProducts);
              estimatedTotalPages = Math.ceil(totalVisibleProducts / PER_PAGE);
            } else {
              // Multi-page: skip unreliable ratio estimate; background scan sets accurate count
              var newShopifyOnPage = Math.min(SHOPIFY_PER_PAGE, newTotalProducts);
              var newVisibleOnPage = allProducts.length;
              var newRatio = newShopifyOnPage > 0 ? newVisibleOnPage / newShopifyOnPage : 1;
              var newEstimatedVisible = Math.max(newVisibleOnPage, Math.round(newTotalProducts * newRatio));
              estimatedTotalPages = Math.ceil(newEstimatedVisible / PER_PAGE);
              // Do not show estimate — countAllVisibleProducts will set the accurate count
            }
          }

          if (allProducts.length > 0) {
            var firstProducts = getVirtualPageProducts(1);
            renderProducts(firstProducts);
            renderPagination(hasNextVirtualPage(1));
            ensureProducts(2 * PER_PAGE);
          } else {
            setProductCountText(0);
            pagContainer.innerHTML = '';
            syncVirtualPageInUrl(1, 'replace');
          }

          hideFilterCounts();
          countFilterValues();
          if (newTotalProducts > 0) {
            countAllVisibleProducts(newTotalProducts);
          }
        }
      });
    }

    window.addEventListener('popstate', function () {
      var targetPage = getVirtualPageFromUrl();
      if (targetPage === currentVirtualPage) return;
      goToPage(targetPage, { historyMode: 'none', scroll: false });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
